import { gql } from '@apollo/client';
import { isAfter } from 'date-fns';
import React from 'react';

import { HTMLContentReplacer } from '../../articleComponents/components/HTMLContentReplacer';
import { Layout } from '../../articleComponents/Layout';
import client from '../../utils/apollo-client';
import { topPaths } from '../../utils/topUrls';

export async function getStaticPaths() {
    if (process.env.APP_ENV !== 'prod') {
        const hashes = [
            '3-ways-to-become-the-intern-supervisor-you-dreamed-of-having',
        ];
        return {
            paths: hashes.map((hash) => ({
                params: {
                    url: [hash],
                },
            })),
            fallback: 'blocking',
        };
    }
    return {
        paths: topPaths,
        fallback: 'blocking',
    };
}

export async function getStaticProps({ params }) {
    try {
        const revalidationTime: number = process.env.APP_ENV === 'prod' ? 24000 : 1;
        const pageRevalicationTime: number = process.env.APP_ENV === 'prod' ? 6000 : 1;
        const [url, articleParam] = params.url;
        const {
            data: { checkForRedirect },
        } = await client.query({
            query: gql`
                query Redirects($url: String!) {
                    checkForRedirect(url: $url) {
                        toUrl
                        permanent
                    }
                }
            `,
            variables: {
                url,
            },
        });

        if (
            checkForRedirect?.toUrl
            && checkForRedirect?.toUrl !== `/career-topics/${url}`
        ) {
            return {
                redirect: {
                    destination: checkForRedirect.toUrl,
                    permanent: checkForRedirect.permanent,
                    revalidate: revalidationTime,
                },
            };
        }
        const {
            data: { editorialContent },
        } = await client.query({
            query: gql`
                query EditorialContent($url: String!) {
                    editorialContent(url: $url) {
                        article {
                            active
                            wink
                            contentTitle
                            contentImage {
                                src
                                credit
                                alt
                            }
                            isSponsor
                            contentBody
                            description
                            contentModifiedDate
                            createdDate
                            metaTitle
                            metaDescription
                            canonicalUrl
                        }
                        paidCompany {
                            companyName
                            companyUrl
                            remoteUrl
                            isSponsor
                            companyId
                        }
                        regwall {
                            type
                            position
                            delaySeconds
                            allowClosing
                            forceLogin
                            title: headerText
                            description: bodyText
                            buttonText
                            overlayColor
                            image
                        }
                        author {
                            image {
                                src
                                alt
                            }
                            authorImage {
                                src
                                alt
                            }
                            active
                            karmaScore
                            authorHash
                            connectWithMeText
                            credential
                            username
                            authorName
                            isVip {
                                isVip
                            }
                        }
                    }
                }
            `,
            variables: {
                url,
            },
        });

        const createdDate = editorialContent?.article?.createdDate;
        const cutoffDate = new Date('04-20-2023');
        if (
            articleParam === 'articles'
            && isAfter(new Date(Number.parseInt(createdDate, 10)), cutoffDate)
        ) {
            return {
                props: {
                    errorPage: true,
                },
                notFound: true,
            };
        }
        if (!editorialContent || !editorialContent?.article?.active) {
            return {
                props: {
                    errorPage: true,
                },
                notFound: true,
                revalidate: 60,
            };
        }
        return {
            props: {
                article: editorialContent,
                parsedContentBody: HTMLContentReplacer(
                    editorialContent?.article?.contentBody
                ),
                hash: url,
                buildId: process.env.BUILD_ID,
                type: 'editorial-content',
            },
            revalidate: pageRevalicationTime,
        };
    } catch (e) {
        return {
            props: {
                errorPage: true,
            },
            notFound: true,
            revalidate: 60,
        };
    }
}

const ArticleBody = ({
    article,
    hash,
    type,
    parsedContentBody,
    errorPage,
    urlRedirect,
    pageName,
}) => {
    const props = {
        article,
        hash,
        type,
        parsedContentBody,
        errorPage,
        urlRedirect,
        pageName,
    };
    return <Layout {...props} />;
};

export default ArticleBody;
